/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IconButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityCompositionSetting } from 'src/core/types/EntitySwagger';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type OverlapMethodCardProps = {
    id?: string;
    title: string;
    description: React.ReactNode;
    isSaveOperationRunning: boolean;
    methodKey: EntityCompositionSetting;
    currentMethod: EntityCompositionSetting;
    onLearnMoreClick: () => void;
    onChangeMethodClick: (methodKey: EntityCompositionSetting) => void;
};

const messages = defineMessages({
    selected: { id: 'clu.overlapMethodCard.selected', defaultMessage: 'Selected' },
    selecting: { id: 'clu.overlapMethodCard.selecting', defaultMessage: 'Selecting' },
    currentMethod: { id: 'clu.overlapMethodCard.currentMethod', defaultMessage: 'Current method' },
    changeMethod: { id: 'clu.overlapMethodCard.changeMethod', defaultMessage: 'Change to this method' },
    learnMoreButtonAriaLabel: { id: 'clu.overlapMethodCard.learnMoreButtonAriaLabel', defaultMessage: 'learnMore' }
});

const theme = getScopedTheme('OverlapMethodCard');
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderRadius: 8, boxShadow: theme.cardBosShadow, width: 360 }
});
const statusContainerStyles = (backgroundColor: string, textColor: string = '') =>
    classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
        root: { background: backgroundColor, color: textColor, borderRadius: '8px 8px 0 0' }
    });

export const OverlapMethodCard = observer((props: OverlapMethodCardProps) => {
    const { title, id, currentMethod, methodKey, description, isSaveOperationRunning, onChangeMethodClick, onLearnMoreClick } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const currentProject = rootStore.currentCluProjectStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const isCurrentlySelectedMethod = currentMethod === methodKey;

    return (
        <Stack id={id || methodKey} styles={containerStyles}>
            {!isSaveOperationRunning && isCurrentlySelectedMethod && (
                <Stack
                    horizontal
                    tokens={{ childrenGap: 10, padding: 11 }}
                    styles={statusContainerStyles(theme.statusBackground, `${theme.statusTextColor} !important`)}>
                    <Icon iconName="SkypeCheck" />
                    <LuisText>{localize(messages.selected)}</LuisText>
                </Stack>
            )}

            {isSaveOperationRunning && isCurrentlySelectedMethod && (
                <Stack horizontal tokens={{ childrenGap: 10, padding: 11 }} styles={statusContainerStyles(theme.inprogressStatusColor)}>
                    <Spinner />
                    <LuisText>{localize(messages.selecting)}</LuisText>
                </Stack>
            )}

            <Stack data-automation-id="cardContainer" tokens={{ padding: 12, childrenGap: 20 }} styles={{ root: { minHeight: 435 } }}>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <LuisText fontWeight={600} variant="large">
                        {title}
                    </LuisText>
                    <IconButton
                        iconProps={{ iconName: 'Info' }}
                        aria-label={title}
                        onClick={() => {
                            onLearnMoreClick();
                            logButtonClick(serviceName, 'Schema', `Learn${methodKey}`, 'EntityOptions', currentProject.id);
                        }}
                    />
                </Stack>

                <StackItem grow>{description}</StackItem>

                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    styles={{ root: { alignSelf: 'flex-start' } }}
                    data-automation-id="saveOverlapButtonLanguageToolTip"
                    hidden={canWrite || isCurrentlySelectedMethod || isSaveOperationRunning}
                    componentSurroundedByToolTip={
                        <PrimaryButton
                            onClick={() => {
                                logButtonClick(serviceName, 'Schema', methodKey, 'EntityOptions', currentProject.id);
                                onChangeMethodClick(methodKey);
                            }}
                            disabled={isCurrentlySelectedMethod || !canWrite || isSaveOperationRunning}>
                            {localize(isCurrentlySelectedMethod ? messages.currentMethod : messages.changeMethod)}
                        </PrimaryButton>
                    }
                />
            </Stack>
        </Stack>
    );
});
